import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInWithCredential,
  GoogleAuthProvider,
  signOut as fbSignOut,
  sendPasswordResetEmail as fbSendPasswordResetEmail,
  sendEmailVerification as fbSendEmailVerification,
  updateProfile as fbUpdateProfile,
  fetchSignInMethodsForEmail as fbFetchSignInMethodsForEmail,
  type User,
} from 'firebase/auth';
import { auth } from '@/config/firebase';

const googleProvider = new GoogleAuthProvider();
// Without this Google skips its own UI whenever the browser already has exactly
// one signed-in account: the popup opens, auto-approves that account and closes
// again in an instant, so a user with the wrong account signed in has no way to
// pick another. The window flow in pages/extension-login-page sends the same
// parameter for the same reason.
googleProvider.setCustomParameters({ prompt: 'select_account' });

export const signInWithEmail = (email: string, password: string) =>
  signInWithEmailAndPassword(auth, email, password);

export const signUpWithEmail = (email: string, password: string) =>
  createUserWithEmailAndPassword(auth, email, password);

export const signInWithGoogle = () => signInWithPopup(auth, googleProvider);

/**
 * Sign in with a Google OAuth access token obtained elsewhere — in practice
 * from the window the panel opens, which runs the Google flow at top level
 * because the embedded panel cannot (see lib/auth/google-signin-window).
 * This is a plain REST exchange: no popup and no /__/auth/iframe helper, so it
 * works inside a COEP-locked, storage-partitioned iframe where the popup and
 * redirect flows cannot.
 */
export const signInWithGoogleAccessToken = (accessToken: string) =>
  signInWithCredential(auth, GoogleAuthProvider.credential(null, accessToken));

/**
 * Readable text for a Firebase Auth failure.
 *
 * The pattern this replaces — strip `Firebase: `, then delete `(auth/...)` —
 * turns the SDK's `"Firebase: Error (auth/popup-blocked)."` into the literally
 * useless string `"Error ."`, hiding the one piece of information that says
 * what to do next. Name the common cases and keep the code for the rest.
 */
export function formatAuthError(err: unknown, fallback = 'Sign-in failed'): string {
  const code = (err as { code?: string } | null)?.code || '';
  switch (code) {
    case 'auth/popup-blocked':
      return 'Your browser blocked the sign-in window. Allow pop-ups for this page, then try again.';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'The sign-in window closed before sign-in finished. Please try again.';
    case 'auth/unauthorized-domain':
      return 'This site is not authorised for Google sign-in. Contact support.';
    case 'auth/network-request-failed':
      return 'Network error while signing in. Check your connection and try again.';
    case 'auth/account-exists-with-different-credential':
      return 'An account already exists with this email using a different sign-in method.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/invalid-credential':
    case 'auth/credential-already-in-use':
      return 'That Google sign-in could not be used. Please try again.';
    default:
      break;
  }
  const raw = err instanceof Error ? err.message.replace('Firebase: ', '').trim() : '';
  if (code) return raw && !/^Error\s*\.?$/.test(raw) ? raw : `${fallback} (${code})`;
  return raw || fallback;
}

export const signOut = () => fbSignOut(auth);

export const onAuthChange = (callback: (user: User | null) => void) =>
  onAuthStateChanged(auth, callback);

export const getCurrentUser = () => auth.currentUser;

export const getIdToken = async (): Promise<string | null> => {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
};

export const sendPasswordResetEmail = (email: string) =>
  fbSendPasswordResetEmail(auth, email);

export const sendVerificationEmail = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');
  await fbSendEmailVerification(user);
};

export const reloadUser = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');
  await user.reload();
  return user;
};

export const updateUserProfile = async (updates: { displayName?: string; photoURL?: string }) => {
  const user = auth.currentUser;
  if (!user) throw new Error('No authenticated user');
  await fbUpdateProfile(user, updates);
  return user;
};

/**
 * Pre-login provider detection — returns the Firebase sign-in methods
 * registered for the given email. Used by the login form to decide
 * whether to show the password field or the "Continue with Google" CTA.
 *
 * Requires Firebase's "Email enumeration protection" to be OFF — with it
 * on, this always returns [] regardless of account existence.
 *
 * Return shape:
 *   ['password']                → email/password account
 *   ['google.com']              → Google-only account
 *   ['password', 'google.com']  → both providers linked
 *   []                           → no account for this email
 */
export const fetchSignInMethodsForEmail = (email: string) =>
  fbFetchSignInMethodsForEmail(auth, email);
